import React, { useState } from "react";
import {
  Box,
  Stack,
} from "@mui/material";
import { StandardTableActions } from "../../../../components/StandardTableActions/StandardTableActions";
import { DataTableV2 } from "../../../../components/TableV2/DataTableV2";
import {
  useTableV2State,
} from "../../../../components/TableV2/hooks/useTableV2State";
import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../../../components/TableV2/interfaces/IDataTableV2Props";
import { TABLE_CACHING_KEYS } from "../../../../constants/constants";
import { useBillingCompanyContext } from "../../../../providers";
import {
  MarketCampaignStatusRenderer,
} from "../../../../components/StatusRenderer/MarketCampaignStatusRenderer";
import { useTimezoneFormatter } from "../../../../hooks/useTimezoneFormatter";
import { useTableData } from "../../../../hooks/useTableData";
import { IPaymentReminderLog } from "../types/IPaymentReminderLog";
import {
  PaymentReminderAutoSearch,
  IPaymentReminderConfigOption,
} from "../../../../components/AutoCompleteSearches/PaymentReminderAutoSearch";
import {
  PaymentReminderNameAutoSearch,
  IPaymentReminderName,
} from "../../../../components/AutoCompleteSearches/PaymentReminderNameAutoSearch";
import { ViewPaymentReminderLogDialog } from "../dialogs/ViewPaymentReminderLogDialog";

/**
 * Tab 2: Sent History
 * Shows logs of all sent payment reminder messages.
 */
export const SentHistoryTable: React.FC = () => {
  const { ftDateTime, ftDate } = useTimezoneFormatter();
  const companyOutlet = useBillingCompanyContext();

  const [selectedReminderConfig, setSelectedReminderConfig] =
    useState<IPaymentReminderConfigOption | null>(null);
  const [openView, setOpenView] = useState<IPaymentReminderLog | null>(null);

  // --- Reminder Name autosearch (distinct reminder names from sent logs) ---
  const [selectedReminderName, setSelectedReminderName] = useState<IPaymentReminderName | null>(null);

  // --- Table state ---
  const {
    state,
    setColumnVisibility,
    onPageChange,
    onRowsPerPageChange,
    onResizeMasterColumns,
    initialColumnVisibility,
  } = useTableV2State({
    filtersInitialState: {
      defaultDateRange: "allTimes",
      selectedTab: "-1",
    },
    pagination: {
      pageNumber: 1,
      rowsInPerPage: 25,
      apiFetchRowCount: 26,
      extraFetchFactor: 1,
    },
    cachingFilters: {
      uniqueKey: TABLE_CACHING_KEYS.PAYMENT_REMINDER_CAMPAIGN_LIST,
    },
  });

  const {
    columnsConfig,
    pagination,
    masterColumnResize,
  } = state;

  const { data: list, loading, totalRecords, refetch } = useTableData<IPaymentReminderLog>({
    path: "/campaign/get-payment-reminder-campaign-logs",
    params: {
      page: pagination.pageNumber,
      rowsPerPage: pagination.rowsInPerPage,
      pageLimit: pagination.apiFetchRowCount,
      company_outlet_uuid: companyOutlet.comapny.company_outlet_uuid,
      type: "PAYMENT_REMINDER",
      whatsapp_campaign_automation_configuration_uuid:
        selectedReminderConfig?.whatsapp_campaign_automation_configuration_uuid,
      market_campaign_uuid: selectedReminderName?.market_campaign_uuid,
    },
  });

  const tableProps: IDataTableV2Props = {
    mobileColumns: [],
    isPagination: true,
    rowsPerPageOptions: pagination.rowsInPerPage,
    isDataLoading: loading,
    extraFetchFactor: pagination.extraFetchFactor,
    totalRecords: totalRecords,
    pageNumber: pagination.pageNumber,
    tableCommandBarProps: {
      customCommandBar: (
        <Stack direction="row" spacing={1} width="100%" alignItems="center">
          <Box sx={{ minWidth: 250 }}>
            <PaymentReminderAutoSearch
              value={selectedReminderConfig?.configuration_name || ""}
              onSelect={(item) => {
                setSelectedReminderConfig(item);
              }}
            />
          </Box>
          <Box sx={{ minWidth: 220 }}>
            <PaymentReminderNameAutoSearch
              value={selectedReminderName?.campaign_name || ""}
              onSelect={(item) => {
                setSelectedReminderName(item);
              }}
            />
          </Box>
        </Stack>
      ),
      preDefinedPlugins: {
        columnVisibility: {
          columnVisibility: columnsConfig.columnVisibility,
          initialColumnVisibility,
          onChange: setColumnVisibility,
        },
        refresh: { onClick: () => refetch() },
      },
      leftItems: { customPlugins: [] },
      rightItems: { customPlugins: [] },
    },
    masterColumns: [
      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        isFirstColumnSticky: true,
        onRowCellRender: (_value, row: IPaymentReminderLog) => (
          <StandardTableActions
            status={row.status}
            onViewClick={() => setOpenView(row)}
          />
        ),
      },
      {
        key: "campaign_name",
        headerName: "Reminder Name",
        fieldName: "campaign_name",
        renderType: DataTableV2RowRenderType.TEXT,
        enableSorting: true,
      },
      {
        key: "name_of_guest",
        headerName: "Guest Name",
        fieldName: "name_of_guest",
        renderType: DataTableV2RowRenderType.TEXT_DARK,
        enableSorting: true,
      },
      {
        key: "recipient_number",
        headerName: "Phone",
        fieldName: "recipient_number",
        renderType: DataTableV2RowRenderType.TEXT,
        enableSorting: true,
      },
      {
        key: "preferred_venue",
        headerName: "Venue",
        fieldName: "preferred_venue",
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "function_date",
        headerName: "Event Date",
        fieldName: "function_date",
        renderType: DataTableV2RowRenderType.DATE,
        enableSorting: true,
        onRowCellRender: (_value, row: IPaymentReminderLog) =>
          row.function_date ? ftDate(row.function_date) : "—",
      },
      {
        key: "template_name",
        headerName: "Template",
        fieldName: "template_name",
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "create_ts",
        headerName: "Sent At",
        fieldName: "create_ts",
        renderType: DataTableV2RowRenderType.DATE_TIME,
        enableSorting: true,
        onRowCellRender: (_value, row: IPaymentReminderLog) =>
          row.create_ts ? ftDateTime(row.create_ts) : "—",
      },
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        enableSorting: true,
        onRowCellRender: (_value, row: IPaymentReminderLog) => (
          <MarketCampaignStatusRenderer status={row.status || ""} />
        ),
      },
    ],
    rows: list,
    onPageChange,
    onRowsPerPageChange,
    masterColumnResize: {
      values: masterColumnResize,
      onResize: onResizeMasterColumns,
    },
  };

  return (
    <>
      <DataTableV2 {...tableProps} />

      {openView && (
        <ViewPaymentReminderLogDialog
          open={true}
          data={openView}
          onClose={() => setOpenView(null)}
        />
      )}
    </>
  );
};
